import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "./ImagePicker";

const EMPTY = { name: "", description: "", image: "", active: true, order: 0 };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/categories", { params: { all: "true" } });
      setCategories(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, order: categories.length }); setEditing("new"); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditing(c.id); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Dê um nome à categoria"); return; }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing === "new") {
        await api.post("/admin/categories", payload);
        toast.success("Categoria criada!");
      } else {
        await api.put(`/admin/categories/${editing}`, payload);
        toast.success("Categoria atualizada!");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir a categoria "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Categoria excluída");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-black text-slate-800">Categorias ({categories.length})</h2>
        <button onClick={openNew} data-testid="new-category-btn" className="bg-brand-gradient text-white text-sm font-bold px-4 py-2.5 rounded-full inline-flex items-center gap-1.5 shadow-soft active:scale-95 transition-transform">
          <Plus size={16} /> Nova categoria
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {categories.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl shadow-card p-4 flex items-center gap-3" data-testid={`admin-category-row-${c.id}`}>
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0">
              {c.image ? <img src={imgUrl(c.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-soft-gradient" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-800 text-sm truncate">
                {c.name}
                {!c.active && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Oculta</span>}
              </p>
              <p className="text-xs text-slate-400 truncate">{c.description}</p>
            </div>
            <button onClick={() => openEdit(c)} aria-label="Editar" data-testid={`edit-category-${c.id}`} className="w-9 h-9 rounded-full hover:bg-pink-50 text-slate-400 hover:text-pink-500 flex items-center justify-center transition-colors"><Pencil size={16} /></button>
            <button onClick={() => remove(c)} aria-label="Excluir" data-testid={`delete-category-${c.id}`} className="w-9 h-9 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-black">{editing === "new" ? "Nova categoria" : "Editar categoria"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 mt-2">
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Nome</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-form-name"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Descrição curta</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-form-description"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Ordem de exibição</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="category-form-order"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" />
            </div>
            <ImagePicker images={form.image} multiple={false} onChange={(img) => setForm({ ...form, image: img })} label="Imagem da categoria" />
            <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
              <span className="text-sm font-bold text-slate-700">Categoria visível no site</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="category-form-active" />
            </div>
            <button onClick={save} disabled={saving} data-testid="category-form-save"
              className="bg-brand-gradient text-white font-bold py-3.5 rounded-full shadow-soft active:scale-95 transition-transform disabled:opacity-60">
              {saving ? "Salvando..." : "Salvar categoria"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
